import React, { createContext, useContext, useMemo } from 'react';
import type { LibraryBlockDef } from '../types';

/**
 * W14 / D41 — the single source of block definitions at author time.
 *
 * Before W14, the builder read a static local library (`FM_LIBRARY_BLOCKS`) that
 * had drifted from the server on 34 of 38 smart blocks. The palette, canvas
 * renderers, field list and condition modal now read the server block catalog
 * threaded through this context. `FormBuilderPage` populates it from
 * `useBlockCatalog()`; there is no fallback to a stale local definition (a failed
 * catalog fetch surfaces an error, exactly like a failed template fetch).
 */
const BlockLibraryContext = createContext<LibraryBlockDef[]>([]);

export function BlockLibraryProvider({
  blocks,
  children,
}: {
  blocks: LibraryBlockDef[];
  children: React.ReactNode;
}): React.JSX.Element {
  return <BlockLibraryContext.Provider value={blocks}>{children}</BlockLibraryContext.Provider>;
}

/** All block definitions served for this clinic (empty until the catalog loads). */
export function useBlockLibrary(): LibraryBlockDef[] {
  return useContext(BlockLibraryContext);
}

/** The definition for one canvas block's `typeId`, or `undefined` if the server no longer serves it. */
export function useLibraryBlock(typeId: string): LibraryBlockDef | undefined {
  const library = useContext(BlockLibraryContext);
  return useMemo(() => library.find((block) => block.id === typeId), [library, typeId]);
}
